import m from 'mithril';
import { MithrilTsxComponent } from 'mithril-tsx-component';
import { animateClassNames, animateCreate, animateRemove } from 'oj-mithriljs-utils';
import { pause } from 'oj-promise-utils';
import { cleanString } from 'oj-utils';
import { base, contact_url, DEFAULT_IMAGE, getFullProduct, getProductName } from "../../state/api";
import state from '../../state/state';
import { getText, getTextFixed, getUrlPrefix } from "../../state/translations";
import { getAsset } from '../../utils/utils';
import { Button } from "../button/button";
import { dialog } from '../dialog/dialog';
import { sharedProductUtils } from "../productDetail/productDetail";
import { Svg } from '../svg/svg';
import { ea } from "../visualization/visualization";
import './footer.scss';
import { findTransformedOptionsNotPossibleCause, parseOption, transformOptions, validateTransformedOptions } from './util';
import { railProContent } from '../prorail/prorail';
const _nKey = (option) => {
    const m = option.match(/^1_(\d+)$/);
    return m
        ? parseInt(m[1], 10)
        : undefined;
};
const optionSort = (a, b) => {
    const na = _nKey(a.option);
    const nb = _nKey(b.option);
    return na !== undefined && nb !== undefined
        ? na - nb
        : 0;
};
class FooterOptions extends MithrilTsxComponent {
    static prevKey = -1;
    static direction = 1;
    activeOption = null;
    dom;
    abort;
    oninit(v) {
        if (v.attrs.key >= FooterOptions.prevKey)
            FooterOptions.direction = 1;
        else if (v.attrs.key < FooterOptions.prevKey)
            FooterOptions.direction = -1;
        FooterOptions.prevKey = v.attrs.key ?? -1;
        this.updateActiveOption(v);
    }
    optionView = (v) => (option, mod = this.getOptionModifiers(option)) => {
        return m("button", { className: cleanString("option", this.activeOption?.option === option.option && "active", mod.isDisabled && "disabled"), onclick: async () => {
                if (mod.isDisabled) {
                    const cause = findTransformedOptionsNotPossibleCause(v.attrs.tabs[v.attrs.tab], option.variants.find(x => x.modifiers.has("NOT_POSSIBLE")), state, v.attrs.tabs);
                    let tabIdx = cause ? v.attrs.tabs.findIndex(t => t.key === cause.key) : undefined;
                    if (tabIdx === -1)
                        tabIdx = 0;
                    const tabName = getText(tabIdx == undefined ? undefined : v.attrs.tabs[tabIdx].label);
                    const r = await dialog({
                        className: "confirm-dialog-disabled",
                        title: getText(option.option),
                        dismiss: true,
                        closeIcon: true
                    }, c => [
                        m("p", null, mod.isNotPossible && cause?.values?.length > 0
                            ? getTextFixed("NOT_POSSIBLE_CHANGE_TO", { tab: tabName, values: cause.values.map(val => getText(String(val))).join("||") })
                            : mod.isNotPossible
                                ? getText(`NOT_POSSIBLE_DESCRIPTION`)
                                : getText(`${option.option}_DESCRIPTION`)),
                        m("div", { className: "dialog-footer" }, tabName
                            ? m(Button, { text: getTextFixed("GO_TO_TAB", { tab: tabName }), className: "button-secondary", onclick: () => {
                                    v.attrs.ontabchange?.(tabIdx);
                                    c();
                                } })
                            : m(Button, { text: getTextFixed("CONTACT_US"), className: "button-secondary", onclick: () => {
                                    const w = window.open(contact_url, "_blank", "noopener,noreferrer");
                                    c();
                                } }))
                    ]).promise;
                    return;
                }
                this.activeOption = option;
                v.attrs.onchange?.(option.variants[0].key);
                await pause(0).promise;
                v.attrs.onheightchange?.(this.dom.scrollHeight);
            } },
            m("span", { className: "fs5" }, getText(option.option)),
            mod.isDisabled && m(Svg, { className: "icon", src: getAsset("icons/info3.svg") }));
    };
    view(v) {
        const options = v.attrs.options.map(x => ({
            x,
            mod: this.getOptionModifiers(x)
        }));
        return m("div", { className: cleanString("FooterOptions", FooterOptions.direction === 1 ? "right" : "left", animateClassNames(v)) },
            m("div", { className: "options" },
                options.filter(o => !o.mod.isNotPossible && !o.mod.isDisabled).map(o => this.optionView(v)(o.x, o.mod)),
                options.filter(o => o.mod.isNotPossible || o.mod.isDisabled).map(o => this.optionView(v)(o.x, o.mod))),
            this.activeOption?.variants.length > 1 && (m("div", { className: "variants" },
                m("span", { className: "fs5" },
                    Object.keys(this.activeOption.variants[0].properties).map(x => getText(x)).join("-"),
                    ":"),
                this.activeOption.variants.map(variant => {
                    const isActive = variant.key === v.attrs.value;
                    const isDisabled = variant.modifiers.has("DISABLED") || variant.modifiers.has("NOT_POSSIBLE");
                    return (m("button", { disabled: isDisabled, className: cleanString("option", isActive && "active", isDisabled && "disabled"), onclick: () => {
                            v.attrs.onchange?.(variant.key);
                        } },
                        m("span", { className: "fs5" }, Object.values(variant.properties).map(p => getText(p.value)).join("-"))));
                }))));
    }
    getOptionModifiers(option) {
        const isNotPossible = option.variants.every(x => x.modifiers.has("NOT_POSSIBLE"));
        const isDisabled = isNotPossible || option.variants.every(x => x.modifiers.has("DISABLED"));
        return { isNotPossible, isDisabled };
    }
    oncreate(v) {
        this.dom = v.dom;
        animateCreate(v);
        this.updateHeight(v);
    }
    onbeforeupdate(v, o) {
        if (v.attrs.value !== o.attrs.value)
            this.updateActiveOption(v);
    }
    async onbeforeremove(v) {
        const dom = v.dom;
        await pause(0).promise;
        dom.classList.remove("right", "left");
        dom.classList.add(FooterOptions.direction === 1 ? "right" : "left");
        return animateRemove(v);
    }
    onremove(v) {
        this.abort?.abort();
    }
    async updateHeight(v) {
        this.abort?.abort();
        this.abort = new AbortController();
        const signal = this.abort.signal;
        let _height;
        const update = () => {
            const _h = this.dom.scrollHeight;
            if (_h !== _height) {
                _height = _h;
                v.attrs.onheightchange?.(_height);
            }
        };
        update();
        await pause(150).promise;
        update();
        while (!signal.aborted) {
            update();
            await pause(500).promise;
        }
    }
    updateActiveOption(v) {
        for (const option of v.attrs.options)
            for (const variant of option.variants)
                if (variant.key === v.attrs.value) {
                    this.activeOption = option;
                    break;
                }
    }
}
class FooterCollapsible extends MithrilTsxComponent {
    dom;
    options;
    oninit(v) {
        if (!(v.attrs.tab in v.attrs.tabs)) {
            v.attrs.ontabchange(0);
            return false;
        }
        this.options = transformOptions(v.attrs.tabs[v.attrs.tab].options)
            .sort(optionSort);
        for (const tab of v.attrs.tabs)
            validateTransformedOptions(tab, transformOptions(tab.options), state);
    }
    view(v) {
        return m("div", { className: cleanString("FooterCollapsible", animateClassNames(v)), style: { height: 0 } },
            m("div", null,
                m(FooterOptions, { key: v.attrs.tab, options: this.options, value: v.attrs.tabs[v.attrs.tab].value, onchange: value => {
                        if (value === v.attrs.tabs[v.attrs.tab].value)
                            return;
                        v.attrs.tabs[v.attrs.tab].update(value);
                        state.setProrailChoice(undefined);
                        v.attrs.onchange?.();
                    }, onheightchange: h => {
                        this.dom.style.height = `${h}px`;
                    }, tabs: v.attrs.tabs, tab: v.attrs.tab, ontabchange: v.attrs.ontabchange })),
            m("button", { className: "collapse-content", onclick: () => v.attrs.onclose?.() },
                m(Svg, { className: "icon", src: getAsset("icons/chevron-down.svg") })));
    }
    oncreate(v) {
        this.dom = v.dom;
        animateCreate(v);
    }
    _values;
    onbeforeupdate(v, o) {
        if (!(v.attrs.tab in v.attrs.tabs)) {
            v.attrs.ontabchange(0);
            return false;
        }
        const values = Object.values(state.wissel).reduce((s, x) => s + x.value, "");
        const valuesChanged = values !== this._values;
        if (valuesChanged || o.attrs.tab !== v.attrs.tab) {
            this.options = transformOptions(v.attrs.tabs[v.attrs.tab].options)
                .sort(optionSort);
            this._values = values;
        }
        if (valuesChanged)
            for (const tab of v.attrs.tabs)
                validateTransformedOptions(tab, transformOptions(tab.options), state);
    }
    onbeforeremove(v) {
        this.dom.style.height = `0px`;
        return animateRemove(v);
    }
}
export class Footer extends MithrilTsxComponent {
    tabs;
    tab;
    isOpen;
    collapsibleEl;
    summary;
    canReset = false;
    dialog;
    windowWidth;
    infoCache = new Map();
    showMobileButtons;
    isProrailPanelOpen = true;
    subs = [];
    oninit(v) {
        this.tabs = state.tabs;
        this.tab = 0;
        this.isOpen = false;
        this.canReset = this.getCanReset();
        this.summary = state.getSummary();
        this.subs.push(ea.on("transition:begin", () => {
            this.showMobileButtons = false;
        }));
        this.subs.push(ea.on("transition:end", () => {
            this.showMobileButtons = true;
        }));
        window.on("resize.footer", () => {
            this.windowWidth = window.innerWidth;
            m.redraw();
        });
        this.subs.push({ off: () => window.off("resize.footer") });
        this.prefetchProducts();
        this.subs.push(state.on("rebuild", () => this.prefetchProducts()));
        this.subs.push(state.on("answers:change", () => this.prefetchProducts()));
    }
    prefetchProducts() {
        for (let product of state.products.base) {
            const variant = state.getVariant(product.category);
            if (variant)
                product = { ...product, ...variant };
            if (this.infoCache.has(product.artCodeTSNL))
                continue;
            this.infoCache.set(product.artCodeTSNL, null);
            getFullProduct(product)
                .then(info => {
                this.infoCache.set(product.artCodeTSNL, info);
                m.redraw();
            })
                .catch(err => console.error(err));
        }
    }
    view(v) {
        // const products=state.wissel.products
        const prorail = state.getProrailChoice();
        return (m("div", { className: "footer" },
            m("div", { className: "options" },
                this.showMobileButtons && window.innerWidth < 768 && m("div", { className: "mobile-products" }, state.products.base.map(product => {
                    const variant = state.getVariant(product.category);
                    if (variant)
                        product = { ...product, ...variant };
                    const fallback = "fallback" in product ? product.fallback : false;
                    const fullProduct = this.infoCache.get(product.artCodeTSNL);
                    return m("button", { id: `product-${product.artCodeTSNL}`, onclick: async (e) => {
                            if (fallback)
                                return dialog({
                                    className: "confirm-dialog-disabled",
                                    title: getText("CONTACT_US"),
                                    dismiss: true,
                                    closeIcon: true
                                }, c => [
                                    m("p", null, getText("PRODUCT_NOT_POSSIBLE_CONTACT_US")),
                                    m("div", { className: "dialog-footer" },
                                        m(Button, { text: getTextFixed("CONTACT_US"), className: "button-secondary", onclick: () => {
                                                const w = window.open(contact_url, "_blank", "noopener,noreferrer");
                                                c();
                                            } }))
                                ]).promise;
                            await sharedProductUtils.openDialog(e, product);
                        }, className: cleanString(fallback && "-fallback") },
                        m("img", { src: fullProduct?.thumbnail ?? DEFAULT_IMAGE, className: cleanString(!fullProduct?.thumbnail && "is-fallback-image") }),
                        m("span", { className: "fs5" }, getProductName(product, fullProduct)));
                })),
                m("div", { className: cleanString("content", !this.isOpen && "closed") },
                    m("div", { className: "tabs", style: { gridTemplateColumns: `repeat(${this.tabs.length}, 1fr) 24px` } }, this.tabs.map((tab, index) => (m("button", { id: `base-option-${tab.key}`, className: cleanString("tab", this.tab === index && this.isOpen && "active"), onclick: e => {
                            if (this.tab === index && this.isOpen) {
                                this.isOpen = false;
                                return;
                            }
                            this.tab = index;
                            this.isOpen = true;
                            e.currentTarget.closest("button").scrollIntoView({
                                behavior: "smooth",
                                block: "nearest",
                                inline: "nearest",
                                container: "nearest"
                            });
                        } },
                        m(Svg, { className: "icon", src: getAsset(tab.icon[this.tab === index && this.isOpen ? 1 : 0]) }),
                        m("span", { className: "fs4" }, getText(tab.label)))))),
                    this.isOpen && m(FooterCollapsible, { tabs: this.tabs, tab: this.tab, onclose: () => { this.isOpen = false; }, ontabchange: tab => this.tab = tab }),
                    m("div", { className: "summary" },
                        m("span", { className: "fs6" }, this.summary),
                        this.canReset && m("button", { onclick: async () => {
                                const r = await dialog({
                                    className: "confirm-dialog",
                                    title: getTextFixed("CONFIRM_DELETE"),
                                    dismiss: false,
                                    closeIcon: true
                                }, c => [
                                    m("p", null, getTextFixed("CONFIGURATION_WILL_BE_DELETED")),
                                    m("div", { className: "dialog-footer" },
                                        m(Button, { text: getTextFixed("NO_DELETE"), className: "button-ghost-default", onclick: () => c() }),
                                        m(Button, { text: getTextFixed("YES_RESET"), className: "button-secondary", onclick: () => c("reset") }))
                                ]).promise;
                                if (r === "reset") {
                                    state.reset();
                                    m.redraw();
                                }
                            } }, getTextFixed("RESET_BUTTON"))))),
            m(Button, { className: "button-default button-black next", onclick: () => {
                    v.attrs.onstep(v.attrs.step + 1);
                } },
                m("span", null, getTextFixed("NEXT_BUTTON")),
                m(Svg, { className: "icon", src: getAsset("icons/arrow-right-white.svg") })),
            prorail &&
                m("div", { className: cleanString("prorail-info-panel", !this.isProrailPanelOpen && "-collapsed"), onclick: async () => {
                        if (!this.isProrailPanelOpen) {
                            this.isProrailPanelOpen = true;
                            m.redraw();
                            return;
                        }
                        await pause(100).promise;
                        const input = document.querySelector(".configurator-header .search input");
                        input.value = prorail.geocode;
                        input.dispatchEvent(new Event("change", {}));
                        input.closest("button").click();
                    } },
                    m("div", { className: "prorail-info-panel-grid" }, railProContent(prorail.geocode, prorail, m(Button, { text: getTextFixed("NOTIFY_PRORAIL_ISSUE"), className: "button-default", onclick: e => {
                            e.stopPropagation();
                            e.preventDefault();
                            import('../notifyProrailIssue/notifyProrailIssue').then(x => x.notify(prorail.geocode, prorail));
                        } }))),
                    m("button", { className: "collapse-content", onclick: e => {
                            e.stopPropagation();
                            this.isProrailPanelOpen = !this.isProrailPanelOpen;
                            m.redraw();
                        } },
                        m(Svg, { className: "icon", src: getAsset("icons/chevron-down.svg") })))));
    }
    async oncreate() {
        await pause(150).promise;
        this.isOpen ||= window.innerWidth >= 1100;
        m.redraw();
    }
    onremove() {
        this.subs.forEach(s => s.off());
    }
    _values;
    onbeforeupdate(v, o) {
        if (v.attrs.step === 1 && o.attrs.step === 0)
            this.isOpen = false;
        const values = Object.values(state.wissel).reduce((s, x) => s + (x.value ?? "_"), "");
        if (values !== this._values) {
            this.canReset = this.getCanReset();
            this.summary = state.getSummary();
            this._values = values;
        }
    }
    getCanReset() {
        const type = parseOption(state.wissel.type.value);
        const dwarsligger = parseOption(state.wissel.wisselLigger.value);
        const hoek = parseOption(state.wissel.hoekVerhouding.value);
        const profiel = parseOption(state.wissel.spoorStaafProfiel.value);
        const straal = parseOption(state.wissel.boogStraal.value);
        return type.key !== state.default.type
            || dwarsligger.key !== state.default.wisselLigger
            || hoek.key !== state.default.hoekVerhouding
            || profiel.key !== state.default.spoorStaafProfiel
            || straal.key !== state.default.boogStraal;
    }
}
